import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store'
import { hasPermission } from '@/utils/permission'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/Login.vue'),
    meta: { title: '登录' }
  },
  {
    path: '/',
    component: () => import('@/components/layout/index.vue'),
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/index.vue'),
        meta: { title: '首页', icon: 'HomeFilled' }
      },
      {
        path: 'user',
        name: 'UserManagement',
        component: () => import('@/views/user/index.vue'),
        meta: { title: '用户管理', icon: 'User', permission: 'user:read' }
      },
      {
        path: 'role',
        name: 'RoleManagement',
        component: () => import('@/views/role/index.vue'),
        meta: { title: '角色管理', icon: 'UserFilled', permission: 'role:read' }
      },
      {
        path: 'permission',
        name: 'PermissionManagement',
        component: () => import('@/views/permission/index.vue'),
        meta: { title: '权限管理', icon: 'Key', permission: 'permission:read' }
      },
      {
        path: 'county-office',
        name: 'CountyOfficeManagement',
        component: () => import('@/views/county-office/index.vue'),
        meta: { title: '县项目办管理', icon: 'OfficeBuilding', permission: 'county-office:read' }
      },
      {
        path: 'county-office-member',
        name: 'CountyOfficeMemberManagement',
        component: () => import('@/views/county-office-member/index.vue'),
        meta: { title: '县项目办成员管理', icon: 'User', permission: 'county-office-member:read' }
      },
      {
        path: 'project-village',
        name: 'ProjectVillageManagement',
        component: () => import('@/views/project-village/index.vue'),
        meta: { title: '项目村管理', icon: 'House', permission: 'project-village:read' }
      },
      {
        path: 'child-archive',
        name: 'ChildArchiveManagement',
        component: () => import('@/views/child-archive/index.vue'),
        meta: { title: '儿童档案管理', icon: 'User', permission: 'child-archive:read' }
      },
      {
        path: 'knowledge-base',
        name: 'KnowledgeBaseManagement',
        component: () => import('@/views/knowledge-base/index.vue'),
        meta: { title: '知识库管理', icon: 'Document', permission: 'knowledge-base:read' }
      },
      {
        path: 'caregiver',
        name: 'CaregiverManagement',
        component: () => import('@/views/caregiver/index.vue'),
        meta: { title: '养育师管理', icon: 'User', permission: 'caregiver:read' }
      },
      {
        path: 'nurturer',
        name: 'NurturerManagement',
        component: () => import('@/views/nurturer/index.vue'),
        meta: { title: '养育人管理', icon: 'User', permission: 'nurturer:read' }
      },
      {
        path: 'administrative-region',
        name: 'AdministrativeRegionManagement',
        component: () => import('@/views/administrative-region/index.vue'),
        meta: { title: '行政地域管理', icon: 'MapLocation', permission: 'administrative-region:read' }
      },
      {
        path: 'splash-screen',
        name: 'SplashScreenManagement',
        component: () => import('@/views/splash-screen/index.vue'),
        meta: { title: '启动页管理', icon: 'Picture', permission: 'splash-screen:read' }
      },
      {
        path: 'data-statistics/operation-statistics',
        name: 'OperationStatistics',
        component: () => import('@/views/data-statistics/operation-statistics/index.vue'),
        meta: { title: '操作数据统计', icon: 'DataAnalysis', permission: 'data-statistics:read' }
      },
      {
        path: 'data-statistics/content-statistics',
        name: 'ContentStatistics',
        component: () => import('@/views/data-statistics/content-statistics/index.vue'),
        meta: { title: '数据内容统计', icon: 'DataAnalysis', permission: 'data-statistics:read' }
      },
      {
        path: 'data-statistics/ai-chat-statistics',
        name: 'AIChatStatistics',
        component: () => import('@/views/data-statistics/ai-chat-statistics/index.vue'),
        meta: { title: 'AI会话统计', icon: 'DataAnalysis', permission: 'data-statistics:read' }
      },
      {
        path: 'data-statistics/role-statistics',
        name: 'RoleStatistics',
        component: () => import('@/views/data-statistics/role-statistics/index.vue'),
        meta: { title: '角色数据统计', icon: 'DataAnalysis', permission: 'role-statistics:read' }
      },
      {
        path: 'feedback',
        name: 'FeedbackManagement',
        component: () => import('@/views/feedback/index.vue'),
        meta: { title: '反馈中心', icon: 'ChatLineRound' }
      },
      {
        path: 'sys-log',
        name: 'SysLogManagement',
        component: () => import('@/views/sys-log/index.vue'),
        meta: { title: '日志管理', icon: 'Document', permission: 'sys-log:read' }
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// 路由守卫
const whiteList = ['/login'] // 白名单，不需要登录

router.beforeEach(async (to, from, next) => {
  // 确保系统配置已加载
  const configLoaded = store.getters.systemConfigLoaded
  if (!configLoaded) {
    try {
      await store.dispatch('system/fetchConfig')
    } catch (error) {
      console.error('路由守卫中加载系统配置失败:', error)
      // 配置加载失败时，如果是登录页则允许继续，否则跳转到登录页
      if (!whiteList.includes(to.path)) {
        next(`/login?redirect=${to.path}`)
        return
      }
    }
  }

  // 设置页面标题
  if (to.meta.title) {
    const config = store.getters.systemConfig
    const siteTitle = config?.admin?.siteTitle || 'Yuer管理系统'
    document.title = `${to.meta.title} - ${siteTitle}`
  }

  const token = store.getters.token
  const user = store.getters.user

  if (whiteList.includes(to.path)) {
    // 如果已登录，跳转到首页
    if (token) {
      next({ path: '/' })
    } else {
      next()
    }
  } else {
    // 需要登录
    if (!token) {
      next(`/login?redirect=${to.path}`)
    } else {
      // 如果有token但没有用户信息，则获取用户信息
      if (!user) {
        try {
          await store.dispatch('user/getInfo')
          // 获取用户信息成功后，检查权限
          if (to.meta.permission) {
            const permissions = store.getters.permissions
            if (!hasPermission(permissions, to.meta.permission)) {
              next({ path: '/403' })
            } else {
              next()
            }
          } else {
            next()
          }
        } catch (error) {
          // 获取用户信息失败，清除token并跳转到登录页
          await store.dispatch('user/resetToken')
          next(`/login?redirect=${to.path}`)
        }
      } else {
        // 已有用户信息，检查权限
        if (to.meta.permission) {
          const permissions = store.getters.permissions
          if (!hasPermission(permissions, to.meta.permission)) {
            next({ path: '/403' })
          } else {
            next()
          }
        } else {
          next()
        }
      }
    }
  }
})

export default router
